// BoostButton — Active Boost trigger + modal pair.
//
// May 25, 2026. Used in Workbench and File Refinery to temporarily upgrade
// the LLM for a single run (or short window). Calls into the existing
// /boost/* endpoints in boost_mode.py.
//
// Props:
//   surface:    "workbench_run" | "refinery" — REQUIRED. Boost is scoped
//               to this surface only, so a workbench boost won't affect
//               refinery and vice-versa.
//   inputChars: estimated input character count, used for cost projection
//               in the modal. Pass 0 if unknown.
//   onBoostChange: optional callback fired with `{active, boost}` when
//               start/cancel completes. Workbench uses this to know
//               whether to skip chunking on the next Run.
//   testidPrefix: optional prefix for data-testid attributes.

import { useEffect, useState } from "react";
import { Lightning, X, ArrowsClockwise, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";

// Tiny inline expandable info tip — eye icon that flips a small block of
// help text on/off. Used inline-in-paragraph so it doesn't grow the
// modal vertically unless the user wants it.
function InlineInfoTip({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center text-[#4A5D4E] hover:text-[#1C1B1A] align-middle"
        title={open ? "Hide details" : "What does this do?"}
        data-testid="inline-info-tip-toggle"
      >
        <Eye size={11} weight="duotone" />
      </button>
      {open && (
        <span className="block mt-1.5 p-2 bg-white rounded border border-[#D9A23A]/40 text-[10px] text-[#4A5D4E] leading-relaxed">
          {children}
        </span>
      )}
    </>
  );
}

// Power models we expose in the modal. Each entry tells us how many
// chars we can feasibly send in a single pass (conservative — leaves
// room for output tokens). When a user picks one with single_pass
// enabled, the surface skips chunking.
//
// pricing_in / pricing_out are in USD per 1M tokens — used to compute
// cost projection. Char→token ratio is ~4:1 for English; we use 0.25
// as the multiplier (chars × 0.25 ≈ tokens) for a rough estimate.
const POWER_MODELS = [
  {
    // Apr 16, 2026 — Anthropic flagship. 1M context, top reasoning, 128k
    // output tokens (~512k chars) per single API call. Recommended for
    // the hardest boost jobs.
    //
    // Feb 28, 2026 — Removed the bogus `-20260416` date suffix from the
    // model id. Anthropic's API returns 404 on the dated form; the
    // canonical alias is just "claude-opus-4-7".
    id: "claude-opus-4-7",
    provider: "anthropic",
    label: "Claude Opus 4.7",
    note: "Anthropic's flagship — 1M context, top reasoning, 128k output.",
    single_pass_chars: 1_000_000,
    max_output_chars: 512_000,    // 128k output tokens × ~4 chars/token
    pricing_in: 5.0,
    pricing_out: 25.0,
    max_output_tokens: 128_000,
  },
  {
    id: "claude-opus-4-6",
    provider: "anthropic",
    label: "Claude Opus 4.6",
    note: "Previous Opus generation — same price + output ceiling as 4.7.",
    single_pass_chars: 1_000_000,
    max_output_chars: 512_000,
    pricing_in: 5.0,
    pricing_out: 25.0,
    max_output_tokens: 128_000,
  },
  {
    id: "claude-sonnet-4-6",
    provider: "anthropic",
    label: "Claude Sonnet 4.6",
    note: "Latest Sonnet — strong reasoning at 1/3 Opus cost. 200k context, 64k output.",
    single_pass_chars: 200_000,
    max_output_chars: 256_000,
    pricing_in: 3.0,
    pricing_out: 15.0,
    max_output_tokens: 64_000,
  },
  {
    id: "claude-sonnet-4-5-20250929",
    provider: "anthropic",
    label: "Claude Sonnet 4.5",
    note: "Previous Sonnet — strong reasoning + 200k context.",
    single_pass_chars: 200_000,
    max_output_chars: 256_000,    // 64k output tokens × ~4 chars/token
    pricing_in: 3.0,
    pricing_out: 15.0,
    max_output_tokens: 64_000,
  },
  {
    id: "gemini-2.5-pro",
    provider: "gemini",
    label: "Gemini 2.5 Pro",
    note: "Google's flagship — massive 1M context, smaller per-call output.",
    single_pass_chars: 800_000,
    max_output_chars: 32_000,     // 8k output tokens × ~4 chars/token
    pricing_in: 1.25,
    pricing_out: 10.0,
    max_output_tokens: 8_192,
  },
  {
    id: "gpt-5.2",
    provider: "openai",
    label: "GPT-5.2",
    note: "OpenAI's latest — 200k context, mid-range per-call output.",
    single_pass_chars: 200_000,
    max_output_chars: 64_000,     // 16k output tokens × ~4 chars/token
    pricing_in: 5.0,
    pricing_out: 15.0,
    max_output_tokens: 16_384,
  },
];

// Jun 2026 — "Hitters": the small/fast models dialed all the way up. Same
// pricing + chunking tool as the heavy hitters, but far cheaper and with big
// context windows — ideal for chewing through giant files when you don't need
// flagship-grade reasoning (bulk trim/condense/cleanup).
const SMALL_MODELS = [
  {
    id: "gemini-3.5-flash-lite",
    provider: "gemini",
    label: "Gemini 3.5 Flash-Lite",
    note: "Cheapest heavy lifter — 1M context, very fast. Best for bulk trim/condense of huge files.",
    single_pass_chars: 1_000_000,
    max_output_chars: 32_000,     // 8k output tokens × ~4 chars/token
    pricing_in: 0.30,
    pricing_out: 2.50,
    max_output_tokens: 8_192,
  },
  {
    id: "gpt-5.4-mini",
    provider: "openai",
    label: "GPT-5.4 mini",
    note: "OpenAI's fast/cheap tier — big context, solid for structured cleanup.",
    single_pass_chars: 600_000,
    max_output_chars: 64_000,     // 16k output tokens × ~4 chars/token
    pricing_in: 0.25,
    pricing_out: 2.0,
    max_output_tokens: 16_384,
  },
  {
    id: "claude-haiku-4-5",
    provider: "anthropic",
    label: "Claude Haiku 4.5",
    note: "Anthropic's fast tier — 200k context, 64k output. Crisp instruction-following on a budget.",
    single_pass_chars: 200_000,
    max_output_chars: 256_000,    // 64k output tokens × ~4 chars/token
    pricing_in: 1.0,
    pricing_out: 5.0,
    max_output_tokens: 64_000,
  },
];

function projectCostCents(model, inputChars, outputRatio = 0.30) {
  // Rough projection — output ratio is configurable so the modal's
  // "desired output" slider can give an honest cost estimate. Default
  // 0.30 matches the typical refinery / workbench output-to-input ratio
  // observed in our usage logs.
  if (!inputChars || inputChars <= 0) return 0;
  const inputTokens = inputChars * 0.25;
  const outputTokens = inputTokens * outputRatio;
  const inputDollars = (inputTokens / 1_000_000) * model.pricing_in;
  const outputDollars = (outputTokens / 1_000_000) * model.pricing_out;
  return Math.ceil((inputDollars + outputDollars) * 100);
}

// Plan how many chunks are needed when the desired total output exceeds
// what a single model call can produce. Returns:
//   chunks       — number of input chunks
//   chunkSize    — chars per input chunk
//   perChunkOut  — chars produced per chunk
//   totalOut     — total chars produced across all chunks
//   needsMerge   — true when chunks > 1 (boundary redundancy risk)
function planRun(model, inputChars, desiredOutputChars) {
  // We are bound by TWO ceilings: the model's input window AND its
  // output cap per call. The smaller of "input that produces desired
  // output" and "input that fits the window" is the chunk size.
  const ratio = inputChars > 0 ? desiredOutputChars / inputChars : 0;
  // Chunk where the per-chunk output stays under the model's max.
  // outputPerChunk = chunkInput × ratio   ≤   max_output_chars
  // ⇒ chunkInput  ≤  max_output_chars / ratio
  const chunkByOutput = ratio > 0
    ? Math.floor(model.max_output_chars / ratio)
    : model.single_pass_chars;
  // chunkSize is the SCHEDULED ceiling. But the actual chunks dispatched
  // can never exceed the real input size. Feb 27, 2026 — Bug fix: was
  // computing totalOut from the model's full single_pass_chars window
  // (e.g., Opus = 1M) regardless of how much input the user actually
  // pasted, so a 362k-char input on Opus showed "150k chars out" when
  // the real run would produce ~54k. Clamp by inputChars.
  const chunkSize = Math.min(model.single_pass_chars, chunkByOutput, inputChars || model.single_pass_chars);
  const chunks = Math.max(1, Math.ceil(inputChars / chunkSize));
  // Per-chunk output: ratio applied to the ACTUAL chunk size, capped at
  // the model's max_output_chars.
  const perChunkOut = Math.min(model.max_output_chars, Math.round(chunkSize * ratio));
  const totalOut = perChunkOut * chunks;
  return {
    chunks,
    chunkSize,
    perChunkOut,
    totalOut,
    needsMerge: chunks > 1,
  };
}

export default function BoostButton({ surface, inputChars = 0, onBoostChange, testidPrefix = "boost", siloId = null, onRequestTopup = null, gateSku = null }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  // Phase B — Creator Pro gate. When the caller passes a gateSku the user
  // doesn't own, the whole Boost control becomes the locked treatment.
  const { locked: cpLocked, lockStyle: cpLockStyle } = useEntitlements();
  const { openGate } = useCreatorProGate();

  // Modal state
  const [picked, setPicked] = useState(POWER_MODELS[0]);
  const [boostTab, setBoostTab] = useState("heavy");
  const [justThisRun, setJustThisRun] = useState(true);
  const [durationMin, setDurationMin] = useState(15);
  const [costCapDollars, setCostCapDollars] = useState(8);
  const [starting, setStarting] = useState(false);
  // May 25, 2026 — Desired output size, as a percentage of input.
  // Drives the per-model chunk plan + cost projection + best-fit
  // recommendation. Default 30% = typical "thoughtful condense".
  // User can slide all the way to 100% for restructure-without-loss work.
  const [outputPct, setOutputPct] = useState(30);
  const desiredOutputChars = Math.round(inputChars * (outputPct / 100));

  const refresh = async () => {
    try {
      // Jul 2026 (Randy) — silo-scoped: only show a boost armed for THIS silo.
      const r = await api.get("/boost/active", { params: siloId ? { silo_id: siloId } : {} });
      const mine = (r.data?.sessions || []).find((s) => s.surface === surface) || null;
      setActive(mine);
      if (onBoostChange) onBoostChange({ active: !!mine, boost: mine });
    } catch {
      // ignore — banner is decorative
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [surface, siloId]);

  // Tick the active banner every 15s so "12m left" stays roughly current
  // without hammering the server.
  useEffect(() => {
    if (!active) return;
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [active?.id]);

  // May 25, 2026 — Auto-prep boundary-redundancy follow-up. When checked
  // AND the picked model would need >1 chunks, after the multi-chunk run
  // completes we load each chunk's output into Slot 1/2/3 (overflow
  // appends to Staging) and auto-select the "Remove boundary redundancy"
  // command. Default ON because boundary redundancy is a near-universal
  // side-effect of multi-chunk runs.
  const [autoPrepRedundancy, setAutoPrepRedundancy] = useState(true);

  // May 25, 2026 — Silo voucher context (Refinery only). Fetched when
  // the modal opens IF a siloId was passed. Used to gate the cost cap
  // against the silo's voucher balance — boost is a platform-key
  // drainer and we don't want a runaway $20 boost on a silo holding
  // only $1.50.
  const [siloCtx, setSiloCtx] = useState(null);
  useEffect(() => {
    if (!open || !siloId) { setSiloCtx(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}/boost-context`);
        if (!cancelled) setSiloCtx(r.data);
      } catch {
        if (!cancelled) setSiloCtx(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, siloId]);

  // Jun 2026 — two-tab boost: "Heavy Hitters" (flagship reasoning) and
  // "Hitters" (small/fast models dialed to the max). The picked model + all
  // projections track whichever tab is active.
  const activeModels = boostTab === "hitters" ? SMALL_MODELS : POWER_MODELS;
  useEffect(() => {
    setPicked((boostTab === "hitters" ? SMALL_MODELS : POWER_MODELS)[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boostTab]);

  const projected = projectCostCents(picked, inputChars, outputPct / 100);

  // May 25, 2026 — Whole-dollar cost cap with a minimum equal to the
  // ceil of the MOST EXPENSIVE model's projection. So no matter which
  // model the user picks, the cap covers it. Example: GPT-5.2 projects
  // $1.04 → min cap = $2.
  const minCostCapDollars = (() => {
    if (inputChars <= 0) return 1;
    const max = activeModels.reduce((m, p) => {
      const c = projectCostCents(p, inputChars, outputPct / 100);
      return Math.max(m, c);
    }, 0);
    return Math.max(1, Math.ceil(max / 100));
  })();
  // Auto-bump the cap if the slider/model changes pushed the minimum
  // above the current value.
  useEffect(() => {
    if (Number(costCapDollars) < minCostCapDollars) setCostCapDollars(minCostCapDollars);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minCostCapDollars]);

  // Voucher gating: BYOK is per-provider on this platform (Feb 27, 2026,
  // Phase 2A). Gemini → creator_api_key, Anthropic → creator_anthropic_key,
  // OpenAI → creator_openai_key. The voucher gate fires when the picked
  // model's provider has NO direct creator BYOK — because then the
  // voucher pays (at 3× markup).
  //
  // Feb 28, 2026 — Per-provider voucher buckets. The cap-vs-voucher check
  // now compares against the bucket matching the picked model's provider
  // (gemini_voucher_cents / anthropic_voucher_cents / openai_voucher_cents)
  // instead of a generic sum.
  const _provider = (picked?.provider || "").toLowerCase();
  const _byokCovers = (() => {
    if (!siloCtx) return false;
    if (_provider === "gemini" || _provider === "google") return !!siloCtx.has_byok;
    if (_provider === "anthropic") return !!siloCtx.has_anthropic_byok;
    if (_provider === "openai") return !!siloCtx.has_openai_byok;
    return false;
  })();
  const _providerBucketCents = (() => {
    if (!siloCtx) return 0;
    if (_provider === "gemini" || _provider === "google") return siloCtx.gemini_voucher_cents || 0;
    if (_provider === "anthropic") return siloCtx.anthropic_voucher_cents || 0;
    if (_provider === "openai") return siloCtx.openai_voucher_cents || 0;
    return 0;
  })();
  // When BYOK covers it, the call goes direct at 1× raw cost. When not,
  // the voucher pays at 3× markup. Display the right number on the modal.
  const PLATFORM_MARKUP_X = 3;
  const projectedDisplayCents = _byokCovers ? projected : projected * PLATFORM_MARKUP_X;
  // Feb 28, 2026 — Race-condition fix. When a siloId is passed but siloCtx
  // hasn't loaded yet, treat the gate as ACTIVE (disable arm). Without
  // this, a slow boost-context fetch lets the user mash Arm before the
  // check runs. Backend also re-checks in /boost/start now, but this keeps
  // the UI honest.
  const _siloCtxPending = !!siloId && siloCtx === null;
  const capExceedsVoucher = !!siloCtx
    && !_byokCovers
    && (Number(costCapDollars) * 100) > _providerBucketCents;

  // May 25, 2026 — Recommend the model that minimizes chunks at the
  // user's desired output size. Tie-breaker: lower cost. Plain "least
  // chunks, cheapest among ties" — easy for the user to reason about.
  const recommendedId = (() => {
    if (inputChars <= 0) return null;
    const ranked = activeModels.map((m) => {
      const p = planRun(m, inputChars, desiredOutputChars);
      const cost = projectCostCents(m, inputChars, outputPct / 100);
      return { id: m.id, chunks: p.chunks, cost };
    }).sort((a, b) => a.chunks - b.chunks || a.cost - b.cost);
    return ranked[0]?.id || null;
  })();

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/boost/start", {
        surface,
        target_model: picked.id,
        target_provider: picked.provider,
        target_max_tokens: picked.max_output_tokens,
        duration_min: Math.max(1, Math.min(240, Number(durationMin) || 15)),
        cost_cap_cents: Math.max(10, Math.min(5000, Math.round(Number(costCapDollars || 0) * 100))),
        just_this_run: !!justThisRun,
        // Feb 28, 2026 — Pass the silo id so the backend can re-check the
        // cost cap against the silo's per-provider voucher bucket. Closes
        // the race-condition bypass when the modal arms before siloCtx
        // finishes loading.
        surface_silo_id: siloId || null,
        // Feb 27, 2026 — Per-chunk desired output target (chars). Backend
        // injects this into the system prompt so Claude actually produces
        // the long-form response the slider promises, instead of deciding
        // 24k chars is "good enough" and stopping.
        desired_output_chars: desiredOutputChars,
      });
      toast.success(`Boost armed — ${picked.label} ready for your next run.`);
      // May 25, 2026 — Stash the auto-prep flag on the local boost object
      // so the Workbench can read it AFTER the run completes and decide
      // whether to load chunks into slots. Server doesn't need to know
      // about this — it's a pure client-side workflow hint.
      const localBoost = { ...r.data, auto_prep_redundancy: !!autoPrepRedundancy };
      setActive(localBoost);
      if (onBoostChange) onBoostChange({ active: true, boost: localBoost });
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start boost.");
    } finally {
      setStarting(false);
    }
  };

  const cancel = async () => {
    if (!active) return;
    setLoading(true);
    try {
      await api.post(`/boost/${active.id}/cancel`);
      toast.success("Boost cancelled.");
      setActive(null);
      if (onBoostChange) onBoostChange({ active: false, boost: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel boost.");
    } finally {
      setLoading(false);
    }
  };

  // Minutes remaining for the active banner
  const minutesLeft = active?.expires_at
    ? Math.max(0, Math.round((new Date(active.expires_at) - new Date()) / 60000))
    : null;

  // Creator Pro gate short-circuit — locked users see the mild treatment and
  // the per-feature upgrade CTA instead of the Boost modal.
  if (gateSku && cpLocked(gateSku)) {
    return (
      <LockGate locked lockStyle={cpLockStyle} onLockedClick={() => openGate(gateSku)}>
        <button
          type="button"
          data-testid={`${testidPrefix}-trigger-btn`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#D9A23A] bg-white text-[#7A5200] text-xs uppercase tracking-[0.15em]"
          title="Creator Pro feature"
        >
          <Lightning size={12} weight="duotone" /> Boost
        </button>
      </LockGate>
    );
  }

  return (
    <>
      {active ? (
        <div className="inline-flex items-center gap-2 bg-[#FEF3CC] border border-[#D9A23A] text-[#7A5200] rounded-md px-3 py-1.5 text-xs"
          data-testid={`${testidPrefix}-active-banner`}>
          <Lightning size={14} weight="duotone" />
          <span>
            Boost active · <strong>{active.target_model}</strong>
            {active.just_this_run ? " · just this run" : ` · ${minutesLeft}m left`}
            {" · $"}{(active.accumulated_cost_cents / 100).toFixed(2)}/${(active.cost_cap_cents / 100).toFixed(2)}
          </span>
          <button
            type="button"
            onClick={cancel}
            disabled={loading}
            data-testid={`${testidPrefix}-cancel-btn`}
            className="ml-2 p-1 hover:bg-[#FBE9A8] rounded"
            title="Cancel boost"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${testidPrefix}-trigger-btn`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#D9A23A] bg-white text-[#7A5200] hover:bg-[#FEF3CC] text-xs uppercase tracking-[0.15em]"
          title="Temporarily swap in a power model for this run"
        >
          <Lightning size={12} weight="duotone" /> Boost
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !starting && setOpen(false)}
          data-testid={`${testidPrefix}-modal-backdrop`}
        >
          <div
            className="bg-white rounded-lg max-w-lg w-full p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[85dvh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testidPrefix}-modal`}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A5200] flex items-center gap-1.5">
                  <Lightning size={12} weight="duotone" /> Boost mode
                </div>
                <h3 className="font-serif-display text-xl text-[#1C1B1A] mt-1">Swap in a power model</h3>
              </div>
              <button onClick={() => !starting && setOpen(false)}
                className="text-[#6B6A68] hover:text-[#1C1B1A] p-1"
                data-testid={`${testidPrefix}-close-btn`}>
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-[#6B6A68] mb-4 leading-relaxed">
              Boost upgrades just this surface ({surface === "workbench_run" ? "Workbench" : "File Refinery"}) to a more powerful model. When the picked model has a huge context window, the run also skips chunking — the whole file goes in one pass.
            </p>

            {/* Desired output size slider */}
            {inputChars > 0 && (
              <div className="mb-4 p-3 border hairline rounded-md bg-[#FAF8F2]">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-[0.15em] text-[#6B6A68]">
                    Desired output size
                  </label>
                  <div className="text-xs tabular-nums text-[#1C1B1A]">
                    <strong>{outputPct}%</strong> of input ·{" "}
                    <span className="text-[#6B6A68]">
                      ~{desiredOutputChars.toLocaleString()} chars
                    </span>
                  </div>
                </div>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={outputPct}
                  onChange={(e) => setOutputPct(parseInt(e.target.value, 10))}
                  data-testid={`${testidPrefix}-output-pct-slider`}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] text-[#6B6A68] mt-1">
                  <span>5% (heavy summary)</span>
                  <span>30% (condense)</span>
                  <span>70% (light trim)</span>
                  <span>100% (restructure)</span>
                </div>
              </div>
            )}

            {/* Model picker */}
            <div className="space-y-2 mb-4">
              {/* Jun 2026 — Hitters / Heavy Hitters tabs */}
              <div className="flex gap-1 p-1 bg-[#F0F1EE] rounded-lg mb-1" data-testid={`${testidPrefix}-tier-tabs`}>
                <button
                  type="button"
                  onClick={() => setBoostTab("hitters")}
                  data-testid={`${testidPrefix}-tab-hitters`}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${boostTab === "hitters" ? "bg-white text-[#1C1B1A] shadow-sm" : "text-[#6B6A68] hover:text-[#1C1B1A]"}`}
                >
                  Hitters
                </button>
                <button
                  type="button"
                  onClick={() => setBoostTab("heavy")}
                  data-testid={`${testidPrefix}-tab-heavy`}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${boostTab === "heavy" ? "bg-white text-[#1C1B1A] shadow-sm" : "text-[#6B6A68] hover:text-[#1C1B1A]"}`}
                >
                  Heavy Hitters
                </button>
              </div>
              <p className="text-[10px] text-[#6B6A68] mb-1.5">
                {boostTab === "hitters"
                  ? "Fast, cheap models dialed to the max — best for chewing through huge files when you don't need flagship reasoning."
                  : "Top-tier reasoning models for the hardest jobs."}
              </p>
              {activeModels.map((m) => {
                const isPicked = m.id === picked.id;
                const isRecommended = m.id === recommendedId;
                const cost = projectCostCents(m, inputChars, outputPct / 100);
                const plan = planRun(m, inputChars, desiredOutputChars);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPicked(m)}
                    data-testid={`${testidPrefix}-model-${m.id}`}
                    className={`w-full text-left border rounded-md p-3 transition-colors ${isPicked ? "border-[#D9A23A] bg-[#FEF3CC]" : "border-[#E5DDC5] bg-white hover:border-[#4A5D4E]"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-[#1C1B1A]">{m.label}</div>
                        {isRecommended && (
                          <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#2F6B3A] text-white font-medium">
                            ★ Best fit
                          </span>
                        )}
                      </div>
                      {inputChars > 0 && (
                        <div className="text-xs tabular-nums text-[#7A5200]">
                          ~${(cost / 100).toFixed(2)}
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-[#6B6A68] mt-1 leading-snug">{m.note}</p>
                    <p className="text-[10px] text-[#6B6A68] mt-0.5">
                      ↳ Up to <strong className="text-[#1C1B1A]">{(m.max_output_chars / 1000).toFixed(0)}k chars</strong> output per chunk
                    </p>
                    {inputChars > 0 && (
                      <div className="text-[10px] mt-1.5 flex items-center gap-1 flex-wrap">
                        {plan.chunks === 1 ? (
                          <span className="text-[#2F6B3A] font-medium">
                            ✓ Single pass · ~{plan.perChunkOut.toLocaleString()} chars out
                          </span>
                        ) : (
                          <>
                            <span className="text-[#4A5D4E] font-medium">
                              {plan.chunks} chunks
                            </span>
                            <span className="text-[#6B6A68]">
                              · ~{plan.perChunkOut.toLocaleString()} chars per chunk · ~{plan.totalOut.toLocaleString()} chars total out
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Multi-chunk hint — recommended next step when picked model
                would produce multiple chunks. Boundary redundancy across
                chunks can be cleaned with a follow-up pass. */}
            {inputChars > 0 && planRun(picked, inputChars, desiredOutputChars).chunks > 1 && (
              <div className="mb-4 p-3 border border-[#D9A23A] bg-[#FEF3CC] rounded-md text-[11px] text-[#7A5200]">
                <strong>Heads up — multi-chunk run.</strong> The {picked.label} pass will produce {planRun(picked, inputChars, desiredOutputChars).chunks} chunks of output. They'll all stream into the Staging area, but adjacent chunks may share repeated ideas at the boundary.
                {/* May 25, 2026 — Checkbox + eye-icon info tip. CANNOT
                    wrap the eye in a <label> because <label> redirects
                    every nested click to the checkbox, defeating
                    e.stopPropagation. Using a click-handler div with
                    explicit checkbox toggle instead. */}
                <div
                  className="flex items-start gap-2 mt-2"
                  data-testid={`${testidPrefix}-auto-prep-wrap`}
                >
                  <input
                    type="checkbox"
                    checked={autoPrepRedundancy}
                    onChange={(e) => setAutoPrepRedundancy(e.target.checked)}
                    data-testid={`${testidPrefix}-auto-prep-checkbox`}
                    className="mt-0.5 cursor-pointer"
                  />
                  <div className="text-[#4A5D4E] flex-1">
                    <span
                      className="cursor-pointer"
                      onClick={() => setAutoPrepRedundancy((v) => !v)}
                    >
                      <strong>Auto-prep boundary-redundancy follow-up</strong>
                    </span>
                    <span className="ml-1.5 inline-block align-middle">
                      <InlineInfoTip>
                        After the multi-chunk run completes, this tool automatically loads each chunk's output into Slot 1, Slot 2, Slot 3 — and appends a complete labeled copy of all chunks to the Staging area. It then auto-selects the <em>Remove boundary redundancy</em> command. With Boost still armed, hit Run a second time and you get back ONE clean document with the chunk seams stitched together (duplicates removed, unique content preserved). Default on because every multi-chunk run has some seam overlap.
                      </InlineInfoTip>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Duration / cost-cap controls */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <label className="block">
                <div className="text-xs text-[#6B6A68] mb-1">Duration</div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="240"
                    value={durationMin}
                    onChange={(e) => setDurationMin(e.target.value)}
                    disabled={justThisRun}
                    data-testid={`${testidPrefix}-duration-input`}
                    className="w-20 border hairline rounded-md px-2 py-1.5 text-sm tabular-nums disabled:bg-[#F0F1EE] disabled:opacity-50"
                  />
                  <span className="text-xs text-[#6B6A68]">min</span>
                </div>
              </label>
              <label className="block">
                <div className="text-xs text-[#6B6A68] mb-1">Cost cap</div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#6B6A68]">$</span>
                  <input
                    type="number"
                    min={minCostCapDollars}
                    max="50"
                    step="1"
                    value={costCapDollars}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!Number.isNaN(v)) setCostCapDollars(Math.max(minCostCapDollars, Math.min(50, v)));
                    }}
                    data-testid={`${testidPrefix}-cost-cap-input`}
                    className="w-24 border hairline rounded-md px-2 py-1.5 text-sm tabular-nums"
                  />
                </div>
                <div className="text-[10px] text-[#6B6A68] mt-1">Min ${minCostCapDollars} · whole-dollar steps</div>
              </label>
            </div>

            {/* May 25, 2026 — Voucher gate for Refinery boost. When the
                cost cap exceeds the silo's voucher and BYOK is NOT set,
                surface a hard-block warning with a "Top up" CTA that
                opens the existing per-silo top-up modal. */}
            {capExceedsVoucher && (
              <div className="mb-4 p-3 border border-[#C16E5A] bg-[#FDF1ED] rounded-md text-[11px] text-[#7A2A20]"
                data-testid={`${testidPrefix}-voucher-gate-warning`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <strong>Cost cap exceeds {siloCtx.silo_name}&apos;s {_provider} bucket.</strong>
                  <span className="tabular-nums text-[10px]">
                    {_provider} bucket: ${(_providerBucketCents / 100).toFixed(2)}
                  </span>
                </div>
                <p className="leading-relaxed">
                  Boost {_provider} calls debit this silo&apos;s {_provider} voucher (no {_provider} BYOK set). At ${costCapDollars} cap, the bucket could run dry mid-run. Lower the cap, switch to a model with BYOK, or top up the {_provider} bucket.
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setCostCapDollars(Math.max(minCostCapDollars, Math.floor(_providerBucketCents / 100)))}
                    data-testid={`${testidPrefix}-cap-snap-to-voucher`}
                    className="px-3 py-1.5 rounded-md border border-[#7A2A20] text-[10px] uppercase tracking-[0.15em] text-[#7A2A20] hover:bg-white"
                  >
                    Lower cap to ${Math.max(minCostCapDollars, Math.floor(_providerBucketCents / 100))}
                  </button>
                  {onRequestTopup && (
                    <button
                      type="button"
                      onClick={() => { setOpen(false); onRequestTopup(siloId, _provider); }}
                      data-testid={`${testidPrefix}-topup-cta`}
                      className="px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white text-[10px] uppercase tracking-[0.15em] hover:bg-[#1C1B1A]"
                    >
                      Top up {_provider} bucket
                    </button>
                  )}
                </div>
              </div>
            )}

            <label className="flex items-start gap-2 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={justThisRun}
                onChange={(e) => setJustThisRun(e.target.checked)}
                data-testid={`${testidPrefix}-just-this-run-checkbox`}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm text-[#1C1B1A]">Just this run</div>
                <div className="text-[11px] text-[#6B6A68]">Boost auto-cancels after the next call. Recommended for one-off heavy lifts.</div>
              </div>
            </label>

            <div className="flex items-center justify-between gap-3 pt-3 border-t hairline">
              <div className="text-[11px] text-[#6B6A68]">
                {inputChars > 0 ? (
                  <>
                    Projected: <strong>~${(projectedDisplayCents / 100).toFixed(2)}</strong>
                    {_byokCovers ? (
                      <span className="ml-1.5 text-[10px] text-[#2F6B3A] bg-[#E8EBE5] px-1.5 py-0.5 rounded uppercase tracking-[0.12em]">BYOK · 1×</span>
                    ) : (
                      <span className="ml-1.5 text-[10px] text-[#7A2A20] bg-[#FDF1ED] px-1.5 py-0.5 rounded uppercase tracking-[0.12em]">Platform · 3×</span>
                    )}
                  </>
                ) : "Pick a model to arm boost."}
              </div>
              <button
                type="button"
                onClick={start}
                disabled={starting || capExceedsVoucher || _siloCtxPending}
                data-testid={`${testidPrefix}-start-btn`}
                className="px-4 py-2 rounded-md bg-[#7A5200] text-white hover:bg-[#5C3E00] text-xs uppercase tracking-[0.15em] disabled:opacity-50 flex items-center gap-1.5"
              >
                {starting ? <ArrowsClockwise size={12} className="animate-spin" /> : <Lightning size={12} weight="duotone" />}
                {starting ? "Arming…" : _siloCtxPending ? "Loading…" : "Arm boost"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Jun 2026 — both tabs' models must be recognized here, or Boost silently
// falls back to the tiny default chunk (the bug where a Hitters-tab pick like
// Gemini Flash only ingested ~10k chars instead of chunking the file properly).
const ALL_BOOST_MODELS = [...POWER_MODELS, ...SMALL_MODELS];

function _boostModel(boost) {
  if (!boost) return null;
  return ALL_BOOST_MODELS.find((m) => m.id === boost.target_model) || null;
}

// Desired output ÷ input, derived from the desired_output_chars captured when
// the boost was armed. Clamped; falls back to 30% (condense) if unknown.
function _boostOutputRatio(boost, inputChars) {
  const desired = Number(boost?.desired_output_chars || 0);
  if (desired > 0 && inputChars > 0) {
    return Math.min(1, Math.max(0.05, desired / inputChars));
  }
  return 0.30;
}

// Jul 2026 (Randy) — exported so the macro editor can let a creator change the
// boosted LLM + output threshold on a saved macro, and so the macro engine can
// flag "heavy-hitter" (expensive) macros and roughly project their cost.
export const HEAVY_BOOST_MODELS = POWER_MODELS;
export const SMALL_BOOST_MODELS = SMALL_MODELS;
export function boostModelLabel(id) {
  const m = ALL_BOOST_MODELS.find((x) => x.id === id);
  return m ? m.label : (id || "");
}
export function boostModelMeta(id) {
  return ALL_BOOST_MODELS.find((x) => x.id === id) || null;
}
export function isHeavyBoostModel(id) {
  return POWER_MODELS.some((m) => m.id === id);
}
export function boostCostEstimateCents(id, inputChars, outputRatio = 0.3) {
  const m = ALL_BOOST_MODELS.find((x) => x.id === id);
  if (!m) return 0;
  return projectCostCents(m, inputChars, outputRatio);
}

// Single pass only when the WHOLE file fits the model's context AND its
// expected output fits the model's per-call output ceiling. The output check
// matters for small models (Flash/mini) whose output cap is low — without it
// a single pass would silently truncate the result.
export function activeBoostAllowsSinglePass(boost, inputChars) {
  const m = _boostModel(boost);
  if (!m || inputChars <= 0) return false;
  const ratio = _boostOutputRatio(boost, inputChars);
  const fitsContext = inputChars <= m.single_pass_chars;
  const fitsOutput = inputChars * ratio <= m.max_output_chars;
  return fitsContext && fitsOutput;
}

// When we can't single-pass, return the BIG chunk size to use instead of the
// default 10k. Capped so each chunk's expected output stays within the model's
// per-call output ceiling — this is what splits a huge file into the projected
// N chunks (e.g. Flash @ 10% → ~320k-char chunks) instead of one truncated run.
export function activeBoostChunkSize(boost, inputChars) {
  const m = _boostModel(boost);
  if (!m) return null;
  const ratio = _boostOutputRatio(boost, inputChars);
  const chunkByOutput = Math.floor(m.max_output_chars / ratio);
  return Math.max(1, Math.min(m.single_pass_chars, chunkByOutput));
}
